import { Component, type ReactNode } from 'react'

/**
 * Guards <HeroCanvas> against failures that the async try/catch in
 * hero-canvas.tsx can't cover: a synchronous throw while constructing
 * WebGPURenderer (seen on browsers that expose `navigator.gpu` +
 * resolve a `requestAdapter()` but choke on actual renderer/context
 * creation — e.g. Opera on some Android builds), or a rejected dynamic
 * import of the hero-canvas chunk (blocked by data-saver mode, an
 * extension, a flaky network, etc.) — React re-throws a rejected
 * `lazy()` import during render, which needs a boundary to catch.
 * Without this, either failure mode takes down the render tree with a
 * blank hero instead of dropping to the static CSS glow.
 *
 * `onError` lets the parent react to a crash (index.tsx retries once on the
 * forced-WebGL2 backend before giving up and showing the CSS fallback). The
 * parent should also change this component's `key` when it retries, since
 * `hasError` is sticky for the life of an instance.
 */
export class HeroErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error('[hero] canvas crashed:', error)
    this.props.onError?.(error)
  }

  render() {
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}
